import querystring from 'querystring';

import {
  IDesign,
  IOrder,
  IRefundRequest,
  IResendRequest,
  ISize,
  ProductType,
} from '../interfaces/Order';
import { OrderInvoice } from '../interfaces/Invoice';
import {
  PaginationParamsRequest,
  PaginationParamsResponse,
} from '../interfaces/PaginationParamsResponse';
import Client from './client';
import { getActiveStoreId } from '../constants/store';

class OrderApi {
  private static classInstance?: OrderApi;

  public static get instance() {
    if (!this.classInstance) {
      this.classInstance = new OrderApi();
    }
    return this.classInstance;
  }

  private withActiveStore<T extends Record<string, any>>(body: T): T {
    const storeId = getActiveStoreId();
    if (!storeId || body?.storeId) {
      return body;
    }
    return { ...body, storeId };
  }

  public getSizes(
    pagination: PaginationParamsRequest & { product?: ProductType }
  ): Promise<PaginationParamsResponse<ISize>> {
    const { page, perPage, all, product } = pagination;
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('perPage', String(perPage));
    if (all) {
      params.set('all', 'true');
    }
    // Only attach the product filter when one was explicitly provided —
    // sending `product=` (empty string) makes the backend filter out rows.
    if (product) {
      params.set('product', product);
    }
    return Client.get(`/size?${params.toString()}`);
  }

  public createSize(body: ISize): Promise<ISize> {
    return Client.post(`/size`, body);
  }

  public updateSize(id: string, body: ISize): Promise<ISize> {
    return Client.patch(`/size/${id}`, body);
  }

  public getSizeDetail(id: string): Promise<ISize> {
    return Client.get(`/size/${id}`);
  }

  public updateSizeOrder(body: ISize[]): Promise<ISize[]> {
    return Client.patch(`/size/reorder`, { sizes: body });
  }

  public getOrders(
    queryParam: any,
    pagination: PaginationParamsRequest
  ): Promise<PaginationParamsResponse<IOrder>> {
    const { page, perPage, all } = pagination;
    return Client.get(
      `/order?${querystring.stringify(
        queryParam
      )}&page=${page}&perPage=${perPage}&${all ? 'all' : ''}`
    );
  }

  public getOrderStatusCount(): Promise<any> {
    return Client.get(`/order/status-counts`);
  }

  public searchOrders(
    queryParam: any,
    pagination: PaginationParamsRequest
  ): Promise<PaginationParamsResponse<IOrder>> {
    const { page, perPage, all } = pagination;
    return Client.get(
      `/order/search?${querystring.stringify(
        queryParam
      )}&page=${page}&perPage=${perPage}&${all ? 'all' : ''}`
    );
  }

  public exportOrdersCsv(
    queryParam: any,
    pagination: PaginationParamsRequest
  ): Promise<string> {
    const { all } = pagination;

    return Client.get(
      `/order/csv?${querystring.stringify(queryParam)}&${all ? 'all' : ''}`
    );
  }

  public importOrderCsv(body: FormData): Promise<Array<IOrder>> {
    return Client.post(`/order/upload/bulk`, body);
  }

  public createOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order`, this.withActiveStore(body));
  }

  public createPhoneCaseOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/phone-case`, this.withActiveStore(body));
  }

  public createPosterOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/poster`, this.withActiveStore(body));
  }

  public createBadgeReelOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/badge-reel`, this.withActiveStore(body));
  }

  public uploadBadgeReelOrderExternal(body: IOrder): Promise<IOrder> {
    return Client.post(
      `/order/badge-reel/external`,
      this.withActiveStore(body)
    );
  }

  public bulkUploadBadgeReelOrdersExternal(
    orders: Array<IOrder>
  ): Promise<Array<IOrder>> {
    return Client.post(
      `/order/badge-reel/external/bulk`,
      this.withActiveStore({ orders })
    );
  }

  public createGlassSuncatcherOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/glass-suncatcher`, this.withActiveStore(body));
  }

  public uploadGlassSuncatcherOrderExternal(body: IOrder): Promise<IOrder> {
    return Client.post(
      `/order/glass-suncatcher/external`,
      this.withActiveStore(body)
    );
  }

  public bulkUploadGlassSuncatcherOrdersExternal(
    orders: Array<IOrder>
  ): Promise<Array<IOrder>> {
    return Client.post(
      `/order/glass-suncatcher/external/bulk`,
      this.withActiveStore({ orders })
    );
  }

  public createSquishyBellyOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/squishy-belly`, this.withActiveStore(body));
  }

  public uploadSquishyBellyOrderExternal(body: IOrder): Promise<IOrder> {
    return Client.post(
      `/order/squishy-belly/external`,
      this.withActiveStore(body)
    );
  }

  public bulkUploadSquishyBellyOrdersExternal(
    orders: Array<IOrder>
  ): Promise<Array<IOrder>> {
    return Client.post(
      `/order/squishy-belly/external/bulk`,
      this.withActiveStore({ orders })
    );
  }

  public createOrnamentOrder(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/ornament`, this.withActiveStore(body));
  }

  public uploadOrnamentOrderExternal(body: IOrder): Promise<IOrder> {
    return Client.post(`/order/ornament/external`, this.withActiveStore(body));
  }

  public bulkUploadOrnamentOrdersExternal(
    orders: Array<IOrder>
  ): Promise<Array<IOrder>> {
    return Client.post(
      `/order/ornament/external/bulk`,
      this.withActiveStore({ orders })
    );
  }

  public updateOrder(id: string, body: IOrder): Promise<IOrder> {
    return Client.patch(`/order/${id}`, body);
  }

  public updateOrderStatus(id: string, status: string): Promise<IOrder> {
    return Client.patch(`/order/${id}/status`, { status });
  }

  public deleteOrder(orderIds: string[]): Promise<IOrder> {
    return Client.post(`/order/delete`, { orderIds });
  }

  public bulkUpdateOrderStatus(
    orderIds: string[],
    status: string
  ): Promise<any> {
    return Client.post(`/order/status/bulk`, {
      orderIds,
      status,
    });
  }

  public bulkUpdateResendRequestStatus(
    requestIds: string[],
    status: string
  ): Promise<any> {
    return Client.post(`/order/resend-request/status/bulk`, {
      requestIds,
      status,
    });
  }

  public getOrderDetail(id: string): Promise<IOrder> {
    return Client.get(`/order/${id}`);
  }

  public getOrderInvoice(id: string): Promise<OrderInvoice> {
    return Client.get(`/order/${id}/invoice`);
  }

  public createDesign(payload: FormData): Promise<IDesign> {
    return Client.post(`/design`, payload);
  }

  public createPhoneCaseDesign(payload: FormData): Promise<IDesign> {
    return Client.post(`/design/phone-case`, payload);
  }

  public uploadDesigns(payload: FormData): Promise<Array<IDesign>> {
    return Client.post(`/design/multiple`, payload);
  }

  public getDesigns(
    pagination: PaginationParamsRequest,
    queryParam: any
  ): Promise<PaginationParamsResponse<IDesign>> {
    const { page, perPage } = pagination;
    return Client.get(
      `/design?${querystring.stringify(
        queryParam
      )}&page=${page}&perPage=${perPage}`
    );
  }

  public updateSku(id: string, sku: string): Promise<any> {
    return Client.patch(`/design/${id}/sku`, { sku });
  }

  public updateMultipleDesignsSku(designs: Array<IDesign>): Promise<void> {
    return Client.patch(`/design/multiple/sku`, { designs: designs });
  }

  public deleteDesign(designs: Array<string>): Promise<any> {
    return Client.post(`/design/delete`, { designs: designs });
  }

  public bulkUpdateTracking(body: FormData): Promise<any> {
    return Client.post(`/order/update-tracking/bulk`, body);
  }

  public createRefundRequest(
    orderId: string,
    payload: FormData
  ): Promise<IRefundRequest> {
    return Client.post(`/order/${orderId}/refund-request`, payload);
  }

  public updateRefundRequest(
    orderId: string,
    id: string,
    payload: FormData
  ): Promise<IRefundRequest> {
    return Client.put(`/order/${orderId}/refund-request/${id}`, payload);
  }

  public updateRefundRequestStatus(
    orderId: string,
    id: string,
    status: string
  ): Promise<IRefundRequest> {
    return Client.patch(`/order/${orderId}/refund-request/${id}/status`, {
      status,
    });
  }

  public createResendRequest(
    orderId: string,
    body: {
      reason?: string;
    }
  ): Promise<IResendRequest> {
    return Client.post(`/order/${orderId}/resend-request`, body);
  }

  public updateResendRequest(
    orderId: string,
    id: string,
    body: {
      reason?: string;
    }
  ): Promise<IResendRequest> {
    return Client.put(`/order/${orderId}/resend-request/${id}`, body);
  }

  public updateResendRequestStatus(
    orderId: string,
    id: string,
    status: string
  ): Promise<IResendRequest> {
    return Client.patch(`/order/${orderId}/resend-request/${id}/status`, {
      status,
    });
  }

  public processImages(body: FormData) {
    return Client.post(`/image`, body, { responseType: 'arraybuffer' });
  }

  public createShippingLabel(orderId: string): Promise<any> {
    return Client.post(`/order/${orderId}/shipping-label`);
  }

  public purchaseShipment(orderId: string, address: string): Promise<any> {
    return Client.post(`/order/${orderId}/purchase-shipment`, { address });
  }

  public purchaseShippoShipment(
    orderId: string,
    address: string,
  ): Promise<any> {
    return Client.post(`/order/${orderId}/purchase-shippo-shipment`, {
      address,
    });
  }

  public bulkPurchaseShippoShipment(orderIds: string[]): Promise<{
    succeeded: IOrder[];
    failed: Array<{ orderId: string; error: string }>;
  }> {
    return Client.post(`/order/purchase-shippo-shipment/bulk`, { orderIds });
  }
}

export default OrderApi.instance;
